import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Tooltip } from 'antd';
import DataTable from '@/components/data-table';
import { API } from '@/services/api.service';
import { date, money } from '../../_components/format';

/**
 * Payments to the supplier, agreeing with the invoices' paid marks: one row per
 * statement line applied to one of its invoices, and one for what the books, an
 * advance or a person closed beyond those lines, each saying its source. Newest
 * first, paged on the server (`pOffset` / `pLimit`).
 */
export default function SupplierPayments({ company, pib, queryParams, setQueryParams }) {
  const { t } = useTranslation('suppliers');
  const page = { offset: String(queryParams.pOffset || '1'), limit: String(queryParams.pLimit || '10') };
  const { data, isLoading, error } = useQuery({
    queryKey: ['suppliers', company, 'payments', pib, page],
    queryFn: () => API.suppliers.getPayments(company, pib, page),
    enabled: Boolean(company && pib),
    placeholderData: keepPreviousData,
    retry: false,
  });

  const columns = [
    { title: t('payments.date'), dataIndex: 'valueDate', key: 'valueDate', width: 110, render: date },
    { title: t('payments.amount'), dataIndex: 'amount', key: 'amount', width: 150, align: 'right', render: money },
    { title: t('payments.currency'), dataIndex: 'currency', key: 'currency', width: 80 },
    {
      title: t('payments.source'),
      dataIndex: 'source',
      key: 'source',
      width: 110,
      render: (v) => t(`payments.sources.${v}`),
    },
    { title: t('payments.invoice'), dataIndex: 'documentNumber', key: 'documentNumber', width: 180, ellipsis: true },
    {
      title: t('payments.purpose'),
      dataIndex: 'purpose',
      key: 'purpose',
      width: 320,
      ellipsis: { showTitle: false },
      render: (v) => (
        <Tooltip title={v} placement="topLeft">
          <span>{v}</span>
        </Tooltip>
      ),
    },
    { title: t('payments.account'), dataIndex: 'counterpartyAccount', key: 'counterpartyAccount', width: 200 },
  ];

  return (
    <div className="grid gap-3">
      <p className="m-0 text-[13px] text-zinc-500">{t('payments.hint')}</p>
      <DataTable
        title={`${t('profile.tabs.payments')}${data ? ` · ${data.count}` : ''}`}
        data={data?.items || []}
        total={data?.count || 0}
        status={isLoading ? 'pending' : error ? 'error' : 'success'}
        columns={columns}
        offset={page.offset}
        limit={page.limit}
        syncUrl={false}
        onChangePagination={(key, value) => setQueryParams(key === 'limit' ? { pLimit: value, pOffset: 1 } : { pOffset: value })}
        tableLayout="fixed"
        scroll={{ x: columns.reduce((s, c) => s + c.width, 0) }}
        size="small"
      />
    </div>
  );
}
